import TableCommonHook from '@op/components/TableCommonHook';
import React, { useMemo } from 'react';
import { DBO_V } from '@op/utils/format';
import { SearchBox, TabPanel, Tabs, StatusTip } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import { cardIcon, tableIcon } from '@op/constants/chart';
import { ClassSpecial } from '@op/constants/common';
import VideoCard from './../components/videoCard';
import SingleTranslateSelect from '@op/components/FilterBoxCommon/filters/languages/singleLauage';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import { LanguageType, PageInfoType } from '@op/types/common';
import { useVideoTranslate } from '../context';
import { DBOM_CONTENT } from '@opm/utils';
import useIntls from '@opm/hooks/useIntls';
import VideoInfoCell from '@opm/components/VideoInfoCell';
import './index.scss';

type VideoCardTableType = {
  sortBy: any;
  setSortBy: (value: any) => void;
  language: LanguageType;
  data?: Array<any>;
  total: number;
  loading?: boolean;
  pageInfo: PageInfoType;
  changePageInfo: (value: PageInfoType) => void;
  cardRecords: Array<any>;
  tableRecords: Array<any>;
};

export default function VideoTableList({
  sortBy,
  setSortBy,
  keyword,
  setKeyword,
  download,
  operation,
  setOperation,
  changePageInfo,
  ...data
}: Omit<VideoCardTableType, 'language'> & {
  operation: 'card' | 'table';
  setOperation: (value: 'card' | 'table') => void;
  download: () => void;
  keyword?: string;
  setKeyword: (value: string) => void;
}) {
  const { intl } = useIntls();
  const { language, setLanguage } = useVideoTranslate();

  return (
    <CardCommon
      className="dbom-video-table-cardCommon"
      alwaysShowOperations
      defaultOperation={operation}
      operations={[
        {
          element: (
            <SearchBox
              onSearch={(value = '') => {
                setKeyword(value);
              }}
              onClear={() => {
                setKeyword('');
              }}
              placeholder={intl({ id: 'Search video' })}
            ></SearchBox>
          ),
        },
        cardIcon,
        tableIcon,
      ]}
      hasDownload
      title={
        <div>
          {operation === 'card' && (
            <SortBySelect
              key="sortBy"
              defaultValue={sortBy?.by}
              onChange={(_key: any, value: any) => {
                setSortBy({ order: 'desc', by: value });
              }}
              defaultParams={{
                btnTextPrefix: intl({ id: 'Sort By' }),
                options: [
                  { text: 'Date', value: 'video_release_time' },
                  { text: 'Views', value: 'tweets_view' },
                  { text: 'Engagement', value: 'engagement' },
                  { text: 'Likes', value: 'tweets_like' },
                ],
              }}
            ></SortBySelect>
          )}
          <SingleTranslateSelect
            defaultValue={language}
            onChange={(key: string, value: LanguageType) => setLanguage(value)}
          ></SingleTranslateSelect>
        </div>
      }
      handleOperationsOnClick={(item: any) => {
        setOperation(item.symbol);
      }}
      handleDownloadOnClick={download}
    >
      <Tabs activeId={operation} className={ClassSpecial.TAB_NONE}>
        <TabPanel id={'card'}>
          <VideoList
            language={language}
            changePageInfo={changePageInfo}
            {...data}
          ></VideoList>
        </TabPanel>
        <TabPanel id={'table'}>
          <VideoTable
            sortBy={sortBy}
            setSortBy={setSortBy}
            changePageInfo={changePageInfo}
            language={language}
            {...data}
          ></VideoTable>
        </TabPanel>
      </Tabs>
    </CardCommon>
  );
}

function VideoList({
  language,
  loading,
  cardRecords,
}: Omit<VideoCardTableType, 'setSortBy' | 'sortBy'>) {
  return (
    <CardCommon
      contentCard
      loading={loading && cardRecords.length === 0}
      loadingHeight={350}
      empty={cardRecords.length === 0}
      emptyHeight={350}
    >
      <>
        <div className="dbom-video-list">
          {cardRecords.map((item: any) => {
            return <VideoCard key={item.url} item={item} language={language} />;
          })}
        </div>
        {loading && cardRecords.length !== 0 && (
          <div className="loading-common dbom-loading-common">
            <StatusTip status="loading" />
          </div>
        )}
      </>
    </CardCommon>
  );
}

function VideoTable({
  sortBy,
  setSortBy,
  language,
  tableRecords = [],
  loading,
  total,
  pageInfo,
  changePageInfo,
}: VideoCardTableType) {
  const { intl, isEn } = useIntls();

  const columns = useMemo(() => {
    const list = [
      {
        key: 'video_release_time',
        header: intl('Video(Sort by Date)'),
        width: 600,
        render: (data: any) => {
          return (
            <VideoInfoCell
              data={data}
              fieldMapping={{
                title: (d: any) => DBOM_CONTENT.videoTitle(language, d),
              }}
            />
          );
        },
      },
      {
        key: 'tweets_view',
        header: intl({ id: 'Views' }),
        align: 'right',
        render: (cvm: any) => {
          return <div>{DBO_V.reviews(cvm.views)}</div>;
        },
      },
      {
        key: 'engagement',
        header: intl({ id: 'Engagement' }),
        align: 'right',
        render: (cvm: any) => {
          return <div>{DBO_V.reviews(cvm.engagement)}</div>;
        },
      },
      {
        key: 'tweets_like',
        header: intl({ id: 'Likes' }),
        align: 'right',
        render: (cvm: any) => {
          return <div>{DBO_V.reviews(cvm.likes)}</div>;
        },
      },
    ];

    return list;
  }, [intl, language]);

  return (
    <TableCommonHook
      sortInfo={[sortBy!, setSortBy]}
      records={tableRecords}
      total={total}
      loading={loading}
      pageInfo={pageInfo}
      setPageInfo={changePageInfo}
      sortColumns={[
        'video_release_time',
        'tweets_view',
        'engagement',
        'tweets_like',
      ]}
      recordKey={(item) =>
        item.video_title +
        item.video_release_time +
        item.anchor_name +
        item.video_cover
      }
      columns={columns}
      minWidth={'auto'}
      tableSticky
      pageSizeOptions={[10, 20, 30, 40, 50, 100, 200]}
    ></TableCommonHook>
  );
}
